import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';
import { UsageStatsService } from '../../services/usageStatsService';
import { fillMissingBillingCycles } from '../../utils/usageUtils';
import { type Column, StandardTable } from '../common/UI';

const EMPTY_OBJECT: Record<string, never> = {};

interface MonthlyUsage {
  [key: string]: {
    tenders?: number;
    reports?: number;
    drafts?: number;
    reviews?: number;
    adhoc_documents?: number;
    generated_cvs?: number;
    section_chats?: number;
    team_searches?: number;
    tender_go_fees?: number;
  };
}

interface EntityData {
  id: string;
  name: string;
  type: 'team' | 'user';
  usage?: {
    monthly?: MonthlyUsage;
  };
}

const HistoricalUsagePanel: React.FC = () => {
  const { account } = useAccount();
  const [data, setData] = useState<EntityData[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState<'team' | 'user'>('team');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const targetClientId = account?.clientId;
        if (!targetClientId) {
          setLoading(false);
          return;
        }

        const usageData = await UsageStatsService.getHistoricalUsageData(
          targetClientId,
          (account?.subscription?.current_period_end as Date | number | string) || new Date()
        );
        setData(usageData as EntityData[]);
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: Error logging is expected here
        console.error('Failed to fetch historical usage data', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [account]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const filteredData = data.filter((d) => d.type === viewType);

  const columns: Column<{
    dateKey: string;
    cycleData: MonthlyUsage[string];
    isCurrent: boolean;
  }>[] = [
    {
      id: 'dateKey',
      label: 'Billing Cycle Ending',
      render: (row) => (
        <>
          {new Date(row.dateKey).toLocaleDateString()}
          {row.isCurrent && (
            <Typography variant="caption" color="primary" sx={{ ml: 1, fontWeight: 'bold' }}>
              (Current)
            </Typography>
          )}
        </>
      ),
    },
    {
      id: 'goFees',
      label: 'Go Fees',
      render: (row) => row.cycleData?.tender_go_fees || 0,
    },
    {
      id: 'tenders',
      label: 'Tenders',
      render: (row) => row.cycleData?.tenders || 0,
    },
    {
      id: 'reports',
      label: 'Reports',
      render: (row) => row.cycleData?.reports || 0,
    },
    {
      id: 'drafts',
      label: 'Drafts',
      render: (row) => row.cycleData?.drafts || 0,
    },
    {
      id: 'reviews',
      label: 'Reviews',
      render: (row) => row.cycleData?.reviews || 0,
    },
    {
      id: 'adhoc',
      label: 'Ad-Hoc Docs',
      render: (row) => row.cycleData?.adhoc_documents || 0,
    },
    {
      id: 'cvs',
      label: 'CVs',
      render: (row) => row.cycleData?.generated_cvs || 0,
    },
    {
      id: 'chats',
      label: 'AI Messages',
      render: (row) => row.cycleData?.section_chats || 0,
    },
    {
      id: 'teamSearches',
      label: 'Team Searches',
      render: (row) => row.cycleData?.team_searches || 0,
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
          Historical Usage Breakdown
        </Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>View By</InputLabel>
          <Select
            value={viewType}
            label="View By"
            onChange={(e) => setViewType(e.target.value as 'team' | 'user')}
          >
            <MenuItem value="team">Teams</MenuItem>
            <MenuItem value="user">Users</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {filteredData.map((entity) => {
        let monthly = { ...(entity.usage?.monthly || EMPTY_OBJECT) };
        monthly = fillMissingBillingCycles(
          monthly,
          account?.subscription?.current_period_end as Date | string | number | undefined
        );

        const sortedCycles = Object.keys(monthly).sort(
          (a, b) => new Date(b).getTime() - new Date(a).getTime()
        );

        const tableData = sortedCycles.map((dateKey, index) => ({
          dateKey,
          cycleData: monthly[dateKey] || {},
          isCurrent: index === 0,
        }));

        return (
          <Box key={entity.id} sx={{ mb: 4 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
              {entity.name}
            </Typography>
            <StandardTable columns={columns} data={tableData} keyExtractor={(row) => row.dateKey} />
          </Box>
        );
      })}

      {filteredData.length === 0 && (
        <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
          No {viewType}s found.
        </Typography>
      )}
    </Box>
  );
};

export default HistoricalUsagePanel;
